import { useEffect, useState } from "react";
import {
  getScheduleUpdates,
  markScheduleUpdateRead,
} from "../../../../services/schedule-updates";
import type { ScheduleUpdateNotification } from "../../../../types/schedule-updates";
import { formatUtcDateRange } from "./eventDateRange";
import "./Itinerary.css";

interface SharedEventsBannerProps {
  /** Open the shared event on the calendar. Omit to render the names without links. */
  onOpenEvent?: (calendarEventId: string) => void;
}

/**
 * Page-level "new tournaments were shared with you" banner for the schedule.
 *
 * This is deliberately NOT the collapsed Updates panel and NOT the per-event banner inside the
 * event view. Both of those require the coach to already be looking at the right place; the whole
 * point of this one is that opening the schedule page shows, with nothing expanded and nothing
 * clicked, every tournament that has been shared since they last looked — BY NAME. So it renders
 * as soon as its fetch resolves, above the calendar, and lists one row per unread `event_shared`
 * notification.
 *
 * Seen-state stays server-authoritative. Unlike the per-event banner this does NOT mark-read on
 * view: the whole surface would vanish out from under the coach on their next visit having never
 * been acted on. A notification is marked read when its row is opened, or when the coach dismisses
 * the banner, which is what "I've seen these" actually means here.
 */
export const SharedEventsBanner = ({ onOpenEvent }: SharedEventsBannerProps) => {
  const [items, setItems] = useState<ScheduleUpdateNotification[]>([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Guard the set-state-after-await race: an unmount mid-flight drops the result rather than
    // writing into a dead render.
    let active = true;
    const controller = new AbortController();

    getScheduleUpdates(true, controller.signal)
      .then((updates) => {
        if (!active) return;
        setItems(updates.filter((u) => u.kind === "event_shared"));
      })
      .catch(() => {
        /* non-fatal: no banner if we can't load updates */
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  if (dismissed || items.length === 0) return null;

  const dismiss = () => {
    setDismissed(true);
    // Fire and forget, one call per shared notice. Marking ALL read is wrong here: it would also
    // swallow unread "the games changed" notices this banner never showed.
    items.forEach((n) => {
      markScheduleUpdateRead(n.id).catch(() => {
        /* non-fatal: stays unread and resurfaces next visit */
      });
    });
  };

  const open = (n: ScheduleUpdateNotification) => {
    markScheduleUpdateRead(n.id).catch(() => {
      /* non-fatal: stays unread and resurfaces next visit */
    });
    setItems((prev) => prev.filter((item) => item.id !== n.id));
    onOpenEvent?.(n.calendarEventId);
  };

  const heading =
    items.length === 1
      ? "A new tournament was shared with your program"
      : `${items.length} new tournaments were shared with your program`;

  return (
    <div className="shared-events" role="status" data-testid="shared-events-banner">
      <div className="shared-events__head">
        <span className="shared-events__icon" aria-hidden="true">
          📣
        </span>
        <span className="shared-events__title" data-testid="shared-events-heading">
          {heading}
        </span>
        <button
          type="button"
          className="shared-events__dismiss"
          onClick={dismiss}
          data-testid="shared-events-dismiss"
        >
          Dismiss
        </button>
      </div>
      <ul className="shared-events__list">
        {items.map((n) => {
          const name = n.eventTitle ?? "Untitled tournament";
          const when =
            n.eventStartsAtUtc && n.eventEndsAtUtc
              ? formatUtcDateRange(
                  n.eventStartsAtUtc,
                  n.eventEndsAtUtc,
                  n.eventIsAllDay ?? false,
                )
              : null;
          return (
            <li key={n.id} className="shared-events__item">
              {onOpenEvent ? (
                <button
                  type="button"
                  className="shared-events__link"
                  onClick={() => open(n)}
                  data-testid={`shared-events-open-${n.id}`}
                >
                  <span className="shared-events__name">{name}</span>
                  {when && <span className="shared-events__when">{when}</span>}
                </button>
              ) : (
                <span className="shared-events__link shared-events__link--static">
                  <span className="shared-events__name">{name}</span>
                  {when && <span className="shared-events__when">{when}</span>}
                </span>
              )}
              {n.itineraryRequestId && (
                <span className="shared-events__tag">The itinerary you asked for</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};
